import { computed } from "vue";
import { showAlert } from "@erp/shared/utils/dialog";
import { getErrorMessage } from "@erp/shared/utils/error";
import { uploadFilesWithMapping, } from "@erp/shared/utils/file/upload-file-with-mapping";
import { setInhouseBidNumber } from "@erp/shared/components/ui/Document/_utils/code-generator";
import { useEstimateWriteTabStore } from "../_pinia/write-tab.store";
import { validateBasicInfo, validateCalcCost, validateBOMList, validateDirectInput, } from "@erp/shared/components/ui/Document/_utils/validator";
import { CALCULATION_TYPE } from "@erp/shared/components/ui/Document/_types/calculation-type.type";
import { useEstimateBasicInfoStore } from "../_pinia/basic-info.store";
import { storeToRefs } from "pinia";
import { useEstimateCalcCostInfoStore } from "../_pinia/calc-cost-info.store";
import { S3_FOLDER } from "@erp/shared/api/generated/wrappers/s3";
import { FILE_CATEGORY } from "@erp/shared/types/file-category.types";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { APPROVAL_PHASE } from "@erp/shared/components/ui/Approval/approval-phase.types";
import { useEstimateAction } from "./useEstimateAction";
import { setApprovalBody } from "@erp/shared/components/ui/Approval/utils/set-approval-body";
import { useEstimateStockCostStore } from "../_pinia/stock-cost.store";
import { MEMBER_TYPE } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member.types";
import { isChecked } from "@erp/shared/components/ui/Approval/utils/is-checked";
/**
 * @description 견적서 서비스 함수
 */
export function useEstimateService() {
    const writeTabStore = useEstimateWriteTabStore();
    const { id, givenName, officePhoneNumber } = useLoginInfo();
    const basicStore = useEstimateBasicInfoStore();
    const { inputInhouseBidNumber, memberInfoRef: estimateMemberInfo, basicInfoValidate, inputApprovalFile, inputCompanyBidNumber, inputDueDate, inputProjectName, inputProjectPeriod, inputRemark, inputBlueprintFile, inputOtherFiles, confirmId, filesInputs, } = storeToRefs(basicStore);
    const { companyAutofill } = basicStore;
    const calcCostStore = useEstimateCalcCostInfoStore();
    const { calculationType, calcCostDetailData, calcCostDirectInputData, totalPrice, directInputTotalCost, laborCosts, directInputValidate, } = storeToRefs(calcCostStore);
    const stockCostStore = useEstimateStockCostStore();
    const { stockCosts } = storeToRefs(stockCostStore);
    const isMaterial = computed(() => calculationType.value === CALCULATION_TYPE.MATERIAL);
    const { createMutation, updateMutation } = useEstimateAction();
    const getNextApprovalPhase = () => {
        const checkerId = estimateMemberInfo.value.find((member) => member.type === MEMBER_TYPE.확인)?.id;
        const approverId = estimateMemberInfo.value.find((member) => member.type === MEMBER_TYPE.승인)?.id;
        if (!checkerId || !approverId) {
            throw new Error("확인자 혹은 승인자 정보가 없습니다.");
        }
        const phase = isChecked(Number(id.value), Number(checkerId), Number(approverId))
            ? APPROVAL_PHASE.미승인
            : APPROVAL_PHASE.미확인;
        return phase;
    };
    const getInhouseBidNumber = async () => {
        const raw = inputInhouseBidNumber.value?.value;
        const meta = inputInhouseBidNumber.value && "chipMeta" in inputInhouseBidNumber.value
            ? inputInhouseBidNumber.value.chipMeta
            : undefined;
        if (meta?.mode === "auto") {
            return setInhouseBidNumber(raw);
        }
        return raw;
    };
    const buildUploadTasks = () => {
        const uploadTasks = [];
        if (inputApprovalFile.value?.value) {
            uploadTasks.push({
                file: inputApprovalFile.value.value,
                category: FILE_CATEGORY.승인서,
                path: S3_FOLDER.ESTIMATE_APPROVAL,
            });
        }
        if (inputBlueprintFile.value?.value) {
            uploadTasks.push({
                file: inputBlueprintFile.value.value,
                category: FILE_CATEGORY.도면,
                path: S3_FOLDER.ESTIMATE_BLUEPRINT,
            });
        }
        if (inputOtherFiles.value?.value &&
            inputOtherFiles.value.value.length > 0) {
            uploadTasks.push({
                file: inputOtherFiles.value.value,
                category: FILE_CATEGORY.기타,
                path: S3_FOLDER.ESTIMATE_ETC,
            });
        }
        return uploadTasks;
    };
    const validateEstimateConfirm = async () => {
        writeTabStore.goToBasicInfo();
        const okBasic = await validateBasicInfo(estimateMemberInfo.value, basicInfoValidate.value, filesInputs.value);
        if (!okBasic) {
            return false;
        }
        if (isMaterial.value) {
            calcCostStore.mergeCalcStockCost(stockCosts.value);
        }
        await writeTabStore.goToCalcCost();
        if (isMaterial.value) {
            const okCalc = await validateCalcCost(calcCostDetailData.value);
            if (!okCalc) {
                return false;
            }
            await writeTabStore.goToBOMList();
            const okBOM = await validateBOMList();
            if (!okBOM) {
                return false;
            }
        }
        else {
            const isValid = directInputValidate.value ||
                calcCostStore.triggerDirectInputValidation();
            const okDirectInput = await validateDirectInput(isValid);
            if (!okDirectInput) {
                return false;
            }
        }
        return true;
    };
    // 견적서 생성
    const createEstimateConfirm = async () => {
        try {
            const isValid = await validateEstimateConfirm();
            if (!isValid) {
                return;
            }
            const uploadTasks = buildUploadTasks();
            const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
            if (failedFiles.length > 0) {
                showAlert(`실패한 파일: ${failedFiles.join(", ")}`, "일부 파일 업로드 실패");
            }
            const inhouseNo = await getInhouseBidNumber();
            const now = new Date().toISOString();
            const phase = getNextApprovalPhase();
            const isAutoChecked = phase === APPROVAL_PHASE.미승인;
            const approvalData = setApprovalBody({
                phase,
                memberInfo: estimateMemberInfo.value,
            });
            const body = {
                creater: id.value,
                confirmation: {
                    ...approvalData,
                    ...(isAutoChecked && { checkedDate: now }),
                    writeType: isMaterial.value ? 0 : 1,
                    inhouseBidNumber: inhouseNo,
                    companyBidNumber: inputCompanyBidNumber.value?.value || "",
                    dueDate: inputDueDate.value?.value || "",
                    projectName: inputProjectName.value?.value || "",
                    projectPeriod: inputProjectPeriod.value?.value || null,
                    remark: inputRemark.value?.value || null,
                    companyId: companyAutofill.companyId,
                    companyName: companyAutofill.companyName || "",
                    managerId: companyAutofill.managerId,
                    managerName: companyAutofill.managerName || "",
                    managerEmail: companyAutofill.managerEmail || "",
                    managerPhoneNumber: companyAutofill.managerPhone || "",
                    approvalFile: successfulFiles?.approval?.s3FileName || null,
                    blueprintFile: successfulFiles?.blueprint?.s3FileName || null,
                    etcFiles: successfulFiles?.etcFiles?.s3FileName || [],
                    createrName: givenName.value,
                    createrPhoneNumber: officePhoneNumber.value,
                    totalPrice: isMaterial.value
                        ? totalPrice.value
                        : directInputTotalCost.value,
                },
                calcJson: isMaterial.value
                    ? calcCostDetailData.value
                    : calcCostDirectInputData.value,
                labor: isMaterial.value ? laborCosts.value : [],
                bom: stockCosts.value,
            };
            createMutation.mutate({ body });
        }
        catch (err) {
            showAlert(getErrorMessage(err), "검증 에러");
            return;
        }
    };
    // 견적서 수정
    const updateEstimateConfirm = async () => {
        try {
            if (!confirmId.value) {
                throw new Error("수정할 수주서 ID가 유효하지 않습니다.");
            }
            const isValid = await validateEstimateConfirm();
            if (!isValid) {
                return;
            }
            const uploadTasks = buildUploadTasks();
            const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
            if (failedFiles.length > 0) {
                showAlert(`실패한 파일: ${failedFiles.join(", ")}`, "일부 파일 업로드 실패");
            }
            const inhouseNo = inputInhouseBidNumber.value?.value;
            const now = new Date().toISOString();
            const phase = getNextApprovalPhase();
            const isAutoChecked = phase === APPROVAL_PHASE.미승인;
            const approvalData = setApprovalBody({
                phase,
                memberInfo: estimateMemberInfo.value,
            });
            const body = {
                modifier: id.value,
                confirmation: {
                    ...approvalData,
                    ...(isAutoChecked && { checkedDate: now }),
                    writeType: isMaterial.value ? 0 : 1,
                    inhouseBidNumber: inhouseNo,
                    companyBidNumber: inputCompanyBidNumber.value?.value || "",
                    dueDate: inputDueDate.value?.value || "",
                    projectName: inputProjectName.value?.value || "",
                    projectPeriod: inputProjectPeriod.value?.value || null,
                    remark: inputRemark.value?.value || null,
                    companyId: companyAutofill.companyId,
                    companyName: companyAutofill.companyName || "",
                    managerId: companyAutofill.managerId,
                    managerName: companyAutofill.managerName || "",
                    managerEmail: companyAutofill.managerEmail || "",
                    managerPhoneNumber: companyAutofill.managerPhone || "",
                    approvalFile: successfulFiles?.approval?.s3FileName || null,
                    blueprintFile: successfulFiles?.blueprint?.s3FileName || null,
                    etcFiles: successfulFiles?.etcFiles?.s3FileName || [],
                    createrName: givenName.value,
                    createrPhoneNumber: officePhoneNumber.value,
                    totalPrice: isMaterial.value
                        ? totalPrice.value
                        : directInputTotalCost.value,
                },
                calcJson: isMaterial.value
                    ? calcCostDetailData.value
                    : calcCostDirectInputData.value,
                labor: isMaterial.value ? laborCosts.value : [],
                bom: stockCosts.value,
            };
            updateMutation.mutate({ id: confirmId.value, body });
        }
        catch (err) {
            showAlert(getErrorMessage(err), "검증 에러");
            return;
        }
    };
    const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
    return { createEstimateConfirm, updateEstimateConfirm, isLoading };
}
